import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
  static targets = [
    "sortMenu", "bulkActionsBar", "selectedCount",
    "bulkDeleteModal", "bulkDeleteCount", "bulkDeleteConfirmationInput", "confirmBulkDeleteButton",
    "adminUserModal", "adminModalTitle", "adminUserForm", "adminUserId",
    "adminEmailField", "adminPasswordField", "adminPasswordConfirmationField", "adminSubmitButton",
    "deleteAdminModal", "deleteAdminEmail", "deleteAdminConfirmationInput", "confirmDeleteAdminButton"
  ]

  static values = { bulkActionUrl: String, adminUsersUrl: String }

  connect() {
    this.deleteAdminId = null
  }

  toggleSort() {
    this.sortMenuTarget.classList.toggle("hidden")
  }

  closeSortOnOutside(event) {
    if (!event.target.closest("[data-sort-trigger]")) {
      this.sortMenuTarget.classList.add("hidden")
    }
  }

  toggleSelectAll(event) {
    this.element.querySelectorAll(".user-checkbox").forEach(cb => { cb.checked = event.target.checked })
    this.updateBulkActions()
  }

  checkboxChanged() {
    this.updateBulkActions()
  }

  closeBulkActions() {
    this.element.querySelectorAll(".user-checkbox").forEach(cb => { cb.checked = false })
    const selectAll = this.element.querySelector("#selectAll")
    if (selectAll) selectAll.checked = false
    this.updateBulkActions()
  }

  updateBulkActions() {
    const count = this.element.querySelectorAll(".user-checkbox:checked").length
    if (count > 0) {
      this.selectedCountTarget.textContent = `${count} user${count !== 1 ? "s" : ""} selected`
      this.bulkActionsBarTarget.classList.remove("translate-y-full")
    } else {
      this.bulkActionsBarTarget.classList.add("translate-y-full")
    }
  }

  bulkAction(event) {
    const action = event.currentTarget.dataset.bulkAction
    const userIds = Array.from(this.element.querySelectorAll(".user-checkbox:checked")).map(cb => cb.value)
    if (userIds.length === 0) { alert("Please select users first"); return }

    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.bulkActionUrlValue

    const csrf = document.createElement("input")
    csrf.type = "hidden"
    csrf.name = "authenticity_token"
    csrf.value = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    form.appendChild(csrf)

    const actionInput = document.createElement("input")
    actionInput.type = "hidden"
    actionInput.name = "bulk_action"
    actionInput.value = action
    form.appendChild(actionInput)

    userIds.forEach(id => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "user_ids[]"
      input.value = id
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
  }

  showBulkDeleteModal() {
    const count = this.element.querySelectorAll(".user-checkbox:checked").length
    if (count === 0) { alert("Please select users to delete first"); return }
    this.bulkDeleteCountTarget.textContent = count
    this.bulkDeleteModalTarget.classList.remove("hidden")
    this.bulkDeleteConfirmationInputTarget.focus()
  }

  closeBulkDeleteModal() {
    this.bulkDeleteModalTarget.classList.add("hidden")
    this.bulkDeleteConfirmationInputTarget.value = ""
    this.toggleBulkDeleteButton()
  }

  toggleBulkDeleteButton() {
    const matches = this.bulkDeleteConfirmationInputTarget.value === "DELETE"
    this.confirmBulkDeleteButtonTarget.disabled = !matches
    this.confirmBulkDeleteButtonTarget.classList.toggle("opacity-50", !matches)
    this.confirmBulkDeleteButtonTarget.classList.toggle("cursor-not-allowed", !matches)
  }

  executeBulkDelete() {
    if (this.bulkDeleteConfirmationInputTarget.value !== "DELETE") {
      alert('Please type "DELETE" to confirm')
      return
    }
    this.closeBulkDeleteModal()
    this.submitBulkAction("delete")
  }

  deactivateInstead() {
    this.closeBulkDeleteModal()
    this.submitBulkAction("deactivate")
  }

  submitBulkAction(action) {
    const fakeEvent = { currentTarget: { dataset: { bulkAction: action } } }
    this.bulkAction(fakeEvent)
  }

  showCreateAdminModal() {
    this.adminUserModalTarget.classList.remove("hidden")
    this.adminUserFormTarget.reset()
    this.adminUserIdTarget.value = ""
    this.adminModalTitleTarget.textContent = "Create Admin User"
    this.adminSubmitButtonTarget.textContent = "Create Admin User"
    this.adminEmailFieldTarget.focus()
  }

  editAdminUser(event) {
    const { id, email } = event.currentTarget.dataset
    this.adminUserModalTarget.classList.remove("hidden")
    this.adminUserIdTarget.value = id
    this.adminEmailFieldTarget.value = email
    this.adminPasswordFieldTarget.value = ""
    this.adminPasswordConfirmationFieldTarget.value = ""
    this.adminModalTitleTarget.textContent = "Edit Admin User"
    this.adminSubmitButtonTarget.textContent = "Update Admin User"
    this.adminEmailFieldTarget.focus()
  }

  closeAdminModal() {
    this.adminUserModalTarget.classList.add("hidden")
    this.adminUserFormTarget.reset()
  }

  submitAdminUserForm() {
    const id = this.adminUserIdTarget.value
    const email = this.adminEmailFieldTarget.value
    const password = this.adminPasswordFieldTarget.value
    const passwordConfirmation = this.adminPasswordConfirmationFieldTarget.value

    if (!email.trim()) { alert("Email is required"); return }

    if (id === "") {
      if (!password || !passwordConfirmation) { alert("Password and confirmation are required"); return }
      if (password !== passwordConfirmation) { alert("Passwords do not match"); return }
    } else {
      if (password && password !== passwordConfirmation) { alert("Passwords do not match"); return }
    }

    const formData = new FormData()
    formData.append("email", email)
    if (password) {
      formData.append("password", password)
      formData.append("password_confirmation", passwordConfirmation)
    }

    const url = id === "" ? this.adminUsersUrlValue : `${this.adminUsersUrlValue}/${id}`
    const method = id === "" ? "POST" : "PATCH"

    fetch(url, {
      method: method,
      body: formData,
      headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content") }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) { this.closeAdminModal(); Turbo.visit(window.location.href, { action: "replace" }) }
      else alert(data.error || "An error occurred")
    })
    .catch(() => alert("An error occurred"))
  }

  showDeleteAdminModal(event) {
    const { id, email } = event.currentTarget.dataset
    this.deleteAdminModalTarget.classList.remove("hidden")
    this.deleteAdminEmailTarget.textContent = email
    this.deleteAdminId = id
    this.deleteAdminConfirmationInputTarget.focus()
  }

  closeDeleteAdminModal() {
    this.deleteAdminModalTarget.classList.add("hidden")
    this.deleteAdminConfirmationInputTarget.value = ""
    this.toggleDeleteAdminButton()
    this.deleteAdminId = null
  }

  toggleDeleteAdminButton() {
    const matches = this.deleteAdminConfirmationInputTarget.value === "DELETE"
    this.confirmDeleteAdminButtonTarget.disabled = !matches
    this.confirmDeleteAdminButtonTarget.classList.toggle("opacity-50", !matches)
    this.confirmDeleteAdminButtonTarget.classList.toggle("cursor-not-allowed", !matches)
  }

  executeDeleteAdmin() {
    if (this.deleteAdminConfirmationInputTarget.value !== "DELETE") {
      alert('Please type "DELETE" to confirm')
      return
    }
    if (!this.deleteAdminId) { alert("Invalid admin user"); return }

    fetch(`${this.adminUsersUrlValue}/${this.deleteAdminId}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content") }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) { this.closeDeleteAdminModal(); Turbo.visit(window.location.href, { action: "replace" }) }
      else alert(data.error || "An error occurred")
    })
    .catch(() => alert("An error occurred"))
  }

  closeModalOnBackdrop(event) {
    if (event.target === this.adminUserModalTarget) this.closeAdminModal()
    if (event.target === this.deleteAdminModalTarget) this.closeDeleteAdminModal()
  }

  navigateToUser(event) {
    if (event.target.closest("[data-stop-propagation]")) return
    const url = event.currentTarget.dataset.userUrl
    if (url) Turbo.visit(url)
  }
}
